import { ApiError, ApiErrorCode, NetworkError } from './api-error';

const baseUrl = import.meta.env.VITE_API_URL.replace(/\/+$/, '');

/** Endpoints that must never trigger the refresh-and-retry path. */
const AUTH_ENDPOINTS = ['/admin/auth/refresh', '/admin/auth/google', '/admin/auth/logout'];

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Internal: prevents a refresh attempt from recursing. */
  allowRefresh?: boolean;
}

/**
 * Fetch wrapper for the Fluently API.
 *
 * `credentials: 'include'` is what carries the httpOnly session cookies. It
 * requires the API to name this origin explicitly in CORS — the spec forbids
 * pairing credentials with a wildcard — so a misconfigured origin fails loudly
 * rather than silently dropping the session.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, allowRefresh = true } = options;

  let response: Response;
  try {
    response = await fetch(`${baseUrl}/v1${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (response.ok) {
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }

  // Access tokens are short-lived and cannot be revoked, so a 401 is the normal
  // way an otherwise-valid session expires. Rotate once and retry rather than
  // bouncing the user to sign-in every fifteen minutes.
  if (
    response.status === 401 &&
    allowRefresh &&
    !AUTH_ENDPOINTS.some((endpoint) => path.startsWith(endpoint))
  ) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(path, { ...options, allowRefresh: false });
    }
  }

  throw await toApiError(response);
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const payload = (await response.json()) as {
      error?: { code?: string; message?: string; details?: string[]; requestId?: string };
    };
    const error = payload.error;

    return new ApiError(
      response.status,
      error?.code ?? ApiErrorCode.INTERNAL_ERROR,
      error?.message ?? 'Something went wrong.',
      error?.details,
      error?.requestId,
    );
  } catch {
    // A non-JSON body means something upstream failed — a proxy, a gateway —
    // rather than the API rejecting the request.
    return new ApiError(
      response.status,
      ApiErrorCode.INTERNAL_ERROR,
      'The server returned an unexpected response.',
    );
  }
}

async function tryRefresh(): Promise<boolean> {
  try {
    await request<{ admin: unknown }>('/admin/auth/refresh', {
      method: 'POST',
      allowRefresh: false,
    });
    return true;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  // For writes that are total rather than partial: a café scene arrives whole,
  // both opening turns included, and sending it twice leaves the same rows.
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  // `delete` is a reserved word, so the method cannot be named after the verb
  // it sends. A 204 comes back as undefined — see the ok branch above.
  del: (path: string) => request<void>(path, { method: 'DELETE' }),
};
